import { useQuery } from '@tanstack/react-query'
import { supabase } from '../../lib/supabase'

export interface BillingPayment {
  id: string
  company_id: string
  stripe_payment_intent_id: string
  stripe_charge_id?: string
  stripe_invoice_id?: string
  stripe_subscription_id?: string
  stripe_customer_id: string
  amount: number
  currency: string
  status: string
  tier?: string
  description?: string
  payment_method?: string
  failure_message?: string
  receipt_url?: string
  created_at: string
  paid_at?: string
}

export interface BillingSubscription {
  id: string
  company_id: string
  stripe_subscription_id: string
  stripe_customer_id: string
  stripe_price_id: string
  tier: string
  status: string
  current_period_start?: string
  current_period_end?: string
  cancel_at_period_end: boolean
  canceled_at?: string
  created_at: string
  updated_at: string
}

export interface BillingInvoice {
  id: string
  company_id: string
  stripe_invoice_id: string
  stripe_subscription_id?: string
  stripe_customer_id: string
  amount_due: number
  amount_paid: number
  amount_remaining: number
  currency: string
  status: string
  tier?: string
  description?: string
  invoice_pdf?: string
  hosted_invoice_url?: string
  created_at: string
  due_date?: string
  paid_at?: string
  period_start?: string
  period_end?: string
}

export interface BillingDashboardStats {
  total_payments: number
  total_revenue: number
  failed_amount: number
  successful_payments: number
  failed_payments: number
  active_subscriptions: number
  canceled_subscriptions: number
  overdue_amount: number
}

// Hook to fetch payments for a company
export function usePayments(companyId: string | undefined) {
  return useQuery({
    queryKey: ['payments', companyId],
    queryFn: async () => {
      if (!companyId) return []

      const { data, error } = await supabase
        .from('stripe_payments')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as BillingPayment[]
    },
    enabled: !!companyId,
  })
}

// Hook to fetch subscriptions for a company
export function useSubscriptions(companyId: string | undefined) {
  return useQuery({
    queryKey: ['subscriptions', companyId],
    queryFn: async () => {
      if (!companyId) return []

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as BillingSubscription[]
    },
    enabled: !!companyId,
  })
}

// Hook to fetch invoices for a company
export function useInvoices(companyId: string | undefined) {
  return useQuery({
    queryKey: ['invoices', companyId],
    queryFn: async () => {
      if (!companyId) return []

      const { data, error } = await supabase
        .from('stripe_invoices')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data as BillingInvoice[]
    },
    enabled: !!companyId,
  })
}

// Hook to fetch billing dashboard stats
export function useBillingStats(companyId: string | undefined) {
  return useQuery({
    queryKey: ['billing-stats', companyId],
    queryFn: async () => {
      if (!companyId) return null

      // Get payments stats
      const { data: payments, error: paymentsError } = await supabase
        .from('stripe_payments')
        .select('amount, status')
        .eq('company_id', companyId)

      if (paymentsError) throw paymentsError

      // Get subscriptions stats
      const { data: subscriptions, error: subsError } = await supabase
        .from('subscriptions')
        .select('status')
        .eq('company_id', companyId)

      if (subsError) throw subsError

      // Get overdue invoices
      const { data: overdueInvoices, error: invoicesError } = await supabase
        .from('stripe_invoices')
        .select('amount_due')
        .eq('company_id', companyId)
        .eq('status', 'open')
        .lt('due_date', new Date().toISOString())

      if (invoicesError) throw invoicesError

      // Calculate stats
      const totalRevenue = payments
        ?.filter(p => p.status === 'succeeded')
        .reduce((sum, p) => sum + p.amount, 0) || 0

      const failedAmount = payments
        ?.filter(p => p.status === 'failed')
        .reduce((sum, p) => sum + p.amount, 0) || 0

      const successfulPayments = payments?.filter(p => p.status === 'succeeded').length || 0
      const failedPayments = payments?.filter(p => p.status === 'failed').length || 0
      const totalPayments = payments?.length || 0

      const activeSubscriptions = subscriptions?.filter(s => s.status === 'active').length || 0
      const canceledSubscriptions = subscriptions?.filter(s => s.status === 'canceled').length || 0

      const overdueAmount = overdueInvoices?.reduce((sum, inv) => sum + inv.amount_due, 0) || 0

      return {
        total_payments: totalPayments,
        total_revenue: totalRevenue,
        failed_amount: failedAmount,
        successful_payments: successfulPayments,
        failed_payments: failedPayments,
        active_subscriptions: activeSubscriptions,
        canceled_subscriptions: canceledSubscriptions,
        overdue_amount: overdueAmount,
      } as BillingDashboardStats
    },
    enabled: !!companyId,
  })
}

// Hook for all companies (admin/billing role)
export function useAllPayments() {
  return useQuery({
    queryKey: ['all-payments'],
    queryFn: async () => {
      const { data, error} = await supabase
        .from('stripe_payments')
        .select(`
          *,
          companies:company_id (
            id,
            name
          )
        `)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data
    },
  })
}

export function useAllSubscriptions() {
  return useQuery({
    queryKey: ['all-subscriptions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscriptions')
        .select(`
          *,
          companies:company_id (
            id,
            name
          )
        `)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data
    },
  })
}

export function useAllBillingStats() {
  return useQuery({
    queryKey: ['all-billing-stats'],
    queryFn: async () => {
      // Get all payments
      const { data: payments, error: paymentsError } = await supabase
        .from('stripe_payments')
        .select('amount, status')

      if (paymentsError) throw paymentsError

      // Get all subscriptions
      const { data: subscriptions, error: subsError } = await supabase
        .from('subscriptions')
        .select('status')

      if (subsError) throw subsError

      // Get all overdue invoices
      const { data: overdueInvoices, error: invoicesError } = await supabase
        .from('stripe_invoices')
        .select('amount_due')
        .eq('status', 'open')
        .lt('due_date', new Date().toISOString())

      if (invoicesError) throw invoicesError

      // Calculate stats
      const totalRevenue = payments
        ?.filter(p => p.status === 'succeeded')
        .reduce((sum, p) => sum + p.amount, 0) || 0

      const failedAmount = payments
        ?.filter(p => p.status === 'failed')
        .reduce((sum, p) => sum + p.amount, 0) || 0

      const successfulPayments = payments?.filter(p => p.status === 'succeeded').length || 0
      const failedPayments = payments?.filter(p => p.status === 'failed').length || 0
      const totalPayments = payments?.length || 0

      const activeSubscriptions = subscriptions?.filter(s => s.status === 'active').length || 0
      const canceledSubscriptions = subscriptions?.filter(s => s.status === 'canceled').length || 0

      const overdueAmount = overdueInvoices?.reduce((sum, inv) => sum + inv.amount_due, 0) || 0

      return {
        total_payments: totalPayments,
        total_revenue: totalRevenue,
        failed_amount: failedAmount,
        successful_payments: successfulPayments,
        failed_payments: failedPayments,
        active_subscriptions: activeSubscriptions,
        canceled_subscriptions: canceledSubscriptions,
        overdue_amount: overdueAmount,
      } as BillingDashboardStats
    },
  })
}
